// まとめて承認 (AC-BSP-118) の対象を、 通常入札の申請セット / ブロック入札のグループから
// `BulkApprovalCase` に写す純関数。 入札制限・売買逆転の事前チェックは単件の承認確認 modal と
// 同じ組み立て (`buildSpotApprovalChecks` / `buildBlockApprovalChecks`) を案件ごとに呼ぶだけで、
// 規則を写さない。 承認可否の最終判定は backend。
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  type BiddingBlockApprovalRequest,
  BiddingBlockBidGroupType,
} from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import type { BiddingSpotApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import type { TFunction } from 'i18next'

import { JEPX_AREA_CODES, convertAreaLabel } from '~/models/area'

import type { ApproverIdentityResolver } from './approverIdentity'
import type { BlockApprovalCheckContext } from './blockApprovalViolation'
import { buildBlockApprovalChecks } from './blockApprovalViolation'
import { blockGroupLegs } from './blockGroupLegs'
import type { BulkApprovalCase } from './bulkApproval'
import type {
  SpotApprovalCheckContext,
  SpotAreaBreakdownRow,
} from './spotApprovalDecision'
import {
  buildSpotApprovalChecks,
  buildSpotAreaBreakdown,
  selectRequestBids,
  summarizeSpotBidRows,
} from './spotApprovalDecision'
import type { SpotBidRow } from './spotBidRow'
import { detectVolumeTotalViolations } from './spotBidValidation'

/**
 * 通常入札の承認待ちの申請セットを案件に写す。 並びは `pendingRequests` のまま (画面の
 * 並び順)。 `ctx.volumeTotalViolations` は呼び出し側が省略した時だけここで
 * 1 回検出する (= 案件ごとに母集合を集計し直さない)。
 */
export const buildSpotBulkCases = ({
  pendingRequests,
  bids,
  ctx,
  identityOf,
  t,
}: {
  pendingRequests: readonly BiddingSpotApprovalRequest[]
  bids: SpotBidRow[]
  ctx: SpotApprovalCheckContext
  identityOf: ApproverIdentityResolver
  t: TFunction
}): BulkApprovalCase[] => {
  const shared: SpotApprovalCheckContext = {
    ...ctx,
    volumeTotalViolations:
      ctx.volumeTotalViolations ??
      detectVolumeTotalViolations(ctx.volumeTotalRows, ctx.limits),
  }
  return pendingRequests.map((request, index) => {
    const requestBids = selectRequestBids(bids, request.id)
    const summary = summarizeSpotBidRows(requestBids)
    const checks = buildSpotApprovalChecks(requestBids, shared, t)
    return {
      id: request.id,
      expectedUpdatedAt: request.updatedAt,
      requestedBy: request.requestedBy,
      title: t('page:biddingSpot.bulkApprove.case.spotTitle', {
        no: index + 1,
      }),
      detail: t('page:biddingSpot.bulkApprove.case.spotDetail', {
        count: summary.count,
      }),
      areaRows: buildSpotAreaBreakdown(requestBids),
      buyMw: summary.buyMw,
      sellMw: summary.sellMw,
      violationSections: checks.violationSections,
      unverifiedSections: checks.unverifiedSections,
      warningSections: checks.warningSections,
      identity: identityOf(request),
    }
  })
}

const BLOCK_KIND_KEYS = {
  [BiddingBlockBidGroupType.UNSPECIFIED]: 'STANDARD',
  [BiddingBlockBidGroupType.BLOCK]: 'STANDARD',
  [BiddingBlockBidGroupType.LINK_BLOCK]: 'LINK',
  [BiddingBlockBidGroupType.LOOP_BLOCK]: 'LOOP',
} as const satisfies Record<BiddingBlockBidGroupType, string>

/** group の leg (札) をエリア別に集計する。 数量は leg のコマ別 MW の単純合算 (AC-BSP-053)。 */
const summarizeBlockGroup = (
  group: BiddingBlockApprovalRequest,
): { areaRows: SpotAreaBreakdownRow[]; buyMw: number; sellMw: number } => {
  const byArea = new Map<number, SpotAreaBreakdownRow>()
  let buyMw = 0
  let sellMw = 0
  for (const leg of blockGroupLegs(group)) {
    const volume = leg.timeSlots.reduce(
      (sum, slot) => sum + Number(slot.volumeMwh?.value ?? '0'),
      0,
    )
    const isBuy = leg.direction === Side.BUY
    const isSell = leg.direction === Side.SELL
    if (isBuy) buyMw += volume
    if (isSell) sellMw += volume
    const row = byArea.get(leg.areaCode) ?? {
      areaCode: leg.areaCode,
      areaLabel: convertAreaLabel(leg.areaCode),
      count: 0,
      buyMw: 0,
      sellMw: 0,
    }
    byArea.set(leg.areaCode, {
      ...row,
      count: row.count + 1,
      buyMw: row.buyMw + (isBuy ? volume : 0),
      sellMw: row.sellMw + (isSell ? volume : 0),
    })
  }
  return {
    areaRows: JEPX_AREA_CODES.flatMap((code) => {
      const row = byArea.get(code)
      return row === undefined ? [] : [row]
    }),
    buyMw,
    sellMw,
  }
}

/** group の全 leg を通したコマ範囲 (最小〜最大のコマ番号)。 コマを持たない group は undefined。 */
const komaRangeOf = (
  group: BiddingBlockApprovalRequest,
): { from: number; to: number } | undefined => {
  const codes = blockGroupLegs(group).flatMap((leg) =>
    leg.timeSlots.map((slot) => slot.timeCode),
  )
  return codes.length === 0
    ? undefined
    : { from: Math.min(...codes), to: Math.max(...codes) }
}

/**
 * ブロック入札の承認待ちグループを案件に写す。 並びは `pendingGroups` のまま。 LINK / LOOP は
 * ペアで 1 件 (AC-BSP-059)。 数千グループでも重くならないよう、 上限数量(コマ計) の母集合の
 * 集計は 1 回だけ行う。
 */
export const buildBlockBulkCases = ({
  pendingGroups,
  ctx,
  identityOf,
  t,
}: {
  pendingGroups: readonly BiddingBlockApprovalRequest[]
  ctx: BlockApprovalCheckContext
  identityOf: ApproverIdentityResolver
  t: TFunction
}): BulkApprovalCase[] => {
  const shared: BlockApprovalCheckContext = {
    ...ctx,
    volumeTotalViolations:
      ctx.volumeTotalViolations ??
      detectVolumeTotalViolations(ctx.volumeTotalRows, ctx.limits),
  }
  return pendingGroups.map((group, index) => {
    const summary = summarizeBlockGroup(group)
    const checks = buildBlockApprovalChecks(group, shared, t)
    const range = komaRangeOf(group)
    const legCount = blockGroupLegs(group).length
    return {
      id: group.id,
      expectedUpdatedAt: group.updatedAt,
      requestedBy: group.requestedBy,
      title: t('page:biddingBlock.bulkApprove.case.title', {
        kind: t(
          `page:biddingBlock.kind.${BLOCK_KIND_KEYS[group.groupType]}` as const,
        ),
        no: index + 1,
      }),
      detail:
        range === undefined
          ? t('page:biddingBlock.bulkApprove.case.detailNoKoma', {
              count: legCount,
            })
          : t('page:biddingBlock.bulkApprove.case.detail', {
              from: range.from,
              to: range.to,
              count: legCount,
            }),
      areaRows: summary.areaRows,
      buyMw: summary.buyMw,
      sellMw: summary.sellMw,
      violationSections: checks.violationSections,
      unverifiedSections: checks.unverifiedSections,
      warningSections: [],
      identity: identityOf(group),
    }
  })
}
